import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  CheckCircle2,
  Lock,
  Scissors,
  Settings,
  Sparkles,
  Subtitles,
  User,
  X,
  Play,
  RotateCcw,
  Star,
  Flame,
  Check,
} from "lucide-react";

const InstagramIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 36,
  color = "#000000",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

export const SoclVslRecreation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Core Theme Palette
  const bgDark = "#080C0A";
  const neonLime = "#D8FF3E";
  const neonGlow = "rgba(216, 255, 62, 0.45)";
  const magentaPink = "#FF2E93";
  const textWhite = "#FFFFFF";
  const textMuted = "#BAC4BE";
  const cardBorder = "1px solid rgba(255, 255, 255, 0.12)";

  // Helper spring
  const sp = (delay: number, damping = 14, stiffness = 170) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 0.9 },
    });

  // Smooth rotation for concentric radar rings in Scene 3
  const radarRotation = (frame * 0.4) % 360;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgDark,
        color: textWhite,
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Original Background Music from Reference */}
      <Audio src={staticFile("socl_ref_audio.mp3")} volume={1.0} />

      {/* 1. Subtle Engineering Grid Background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(216, 255, 62, 0.045) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(216, 255, 62, 0.045) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* 2. Top-Right Ambient Radial Glow */}
      <div
        style={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 800,
          height: 800,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(216, 255, 62, 0.14) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      {/* 3. Bottom-Left Secondary Ambient Radial Glow */}
      <div
        style={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(216, 255, 62, 0.09) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      {/* 4. Top-Left Brand Pill [Logo] SCL VSL */}
      <div
        style={{
          position: "absolute",
          top: 70,
          left: 65,
          zIndex: 100,
          display: "inline-flex",
          alignItems: "center",
          gap: 12,
          backgroundColor: "rgba(18, 25, 20, 0.85)",
          padding: "10px 22px",
          borderRadius: 30,
          border: cardBorder,
          backdropFilter: "blur(12px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${neonLime} 0%, #0D1611 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 900, color: "#000000" }}>S</span>
        </div>
        <span
          style={{
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "0.05em",
            color: "#FFFFFF",
          }}
        >
          SCL VSL
        </span>
      </div>

      {/* ========================================================================= */}
      {/* SCENE 1: FRAMES 0 - 100 (0.00s - 3.33s) */}
      {/* "Masih bayar aplikasi" + Stacked Transaction Cards (Jan - Mei) */}
      {/* ========================================================================= */}
      {frame >= 0 && frame < 100 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 240,
          }}
        >
          {/* Main Headline */}
          <h1
            style={{
              fontSize: 68,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              margin: 0,
              textAlign: "center",
              transform: `translateY(${interpolate(sp(0), [0, 1], [30, 0])}px)`,
              opacity: sp(0),
              textShadow: "0 10px 30px rgba(0,0,0,0.7)",
            }}
          >
            Masih bayar aplikasi
          </h1>

          {/* Staggered Stacked Notification Cards */}
          <div
            style={{
              position: "relative",
              width: 860,
              marginTop: 65,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "center",
            }}
          >
            {[
              { month: "Jan", delay: 4, rot: -2.2 },
              { month: "Feb", delay: 14, rot: 2.0 },
              { month: "Mar", delay: 24, rot: -1.6 },
              { month: "Apr", delay: 34, rot: 1.8 },
              { month: "Mei", delay: 44, rot: -0.8 },
            ].map((item, idx) => {
              const cardSp = sp(item.delay, 12, 190);
              const cardY = interpolate(cardSp, [0, 1], [-80, 0]);
              const cardScale = interpolate(cardSp, [0, 1], [0.85, 1]);

              if (frame < item.delay) return null;

              return (
                <div
                  key={idx}
                  style={{
                    width: "100%",
                    height: 98,
                    backgroundColor: "#FFFFFF",
                    color: "#000000",
                    borderRadius: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 30px",
                    boxShadow: "0 14px 34px rgba(0,0,0,0.55)",
                    transform: `translateY(${cardY}px) scale(${cardScale}) rotate(${item.rot}deg)`,
                    transformOrigin: "center center",
                    zIndex: idx + 10,
                  }}
                >
                  {/* Left Month Badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        backgroundColor: "#0D120F",
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 22,
                          fontWeight: 900,
                          color: neonLime,
                          letterSpacing: "0.02em",
                        }}
                      >
                        {item.month}
                      </span>
                    </div>

                    {/* Middle Description */}
                    <span
                      style={{
                        fontSize: 27,
                        fontWeight: 700,
                        color: "#18211C",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      Langganan aplikasi clipper
                    </span>
                  </div>

                  {/* Right Red Deduction */}
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 900,
                      color: "#E63946",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    -Rp300.000
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 2: FRAMES 100 - 180 (3.33s - 6.00s) */}
      {/* "Setahun bisa *tembus* jutaan." + "Rp3.600.000" (Strikethrough in magenta) */}
      {/* ========================================================================= */}
      {frame >= 100 && frame < 180 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 60px",
            textAlign: "center",
          }}
        >
          {/* Headline */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              transform: `scale(${sp(100)})`,
            }}
          >
            <div style={{ fontSize: 64, fontWeight: 800, color: textWhite }}>
              Setahun bisa{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 84,
                  fontWeight: 400,
                  marginLeft: 6,
                }}
              >
                tembus
              </span>
            </div>
            <div
              style={{
                fontSize: 88,
                fontWeight: 900,
                color: neonLime,
                textShadow: `0 0 35px ${neonGlow}`,
                lineHeight: 1,
              }}
            >
              jutaan.
            </div>
          </div>

          {/* Strikethrough Price Presentation */}
          <div
            style={{
              marginTop: 70,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              transform: `scale(${sp(108)})`,
            }}
          >
            {/* The Huge Price */}
            <div
              style={{
                fontSize: 118,
                fontWeight: 900,
                fontFamily: "'Anton', sans-serif",
                letterSpacing: "0.02em",
                color: neonLime,
                textShadow: `0 0 45px ${neonGlow}`,
                position: "relative",
                display: "inline-block",
                padding: "0 10px",
              }}
            >
              Rp3.600.000
              {/* Dynamic Magenta Strikethrough Line */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: "54%",
                  height: 10,
                  backgroundColor: magentaPink,
                  borderRadius: 6,
                  boxShadow: `0 0 20px ${magentaPink}`,
                  width: `${interpolate(frame, [118, 142], [0, 100], {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  })}%`,
                }}
              />
            </div>

            {/* Subtext */}
            <span
              style={{
                marginTop: 18,
                fontSize: 28,
                fontWeight: 600,
                color: textMuted,
                letterSpacing: "0.04em",
              }}
            >
              biaya langganan / tahun
            </span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 3: FRAMES 180 - 315 (6.00s - 10.50s) */}
      {/* "Di SCL VSL, cukup bayar *sekali.*" + Glossy Icon + Orbital Rings */}
      {/* ========================================================================= */}
      {frame >= 180 && frame < 315 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {/* Orbital Radar Rings behind the App Icon */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -50%) rotate(${radarRotation}deg)`,
              width: 820,
              height: 820,
              pointerEvents: "none",
            }}
          >
            {[520, 680, 820].map((size, idx) => (
              <div
                key={idx}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  border: `1.5px dashed rgba(216, 255, 62, ${0.35 - idx * 0.08})`,
                }}
              />
            ))}
          </div>

          {/* Central Glossy App Icon */}
          <div
            style={{
              transform: `scale(${sp(180)})`,
              width: 170,
              height: 170,
              borderRadius: 44,
              background: "linear-gradient(145deg, #1A241E 0%, #0C120F 100%)",
              border: `2px solid ${neonLime}`,
              boxShadow: `0 0 50px rgba(216, 255, 62, 0.4), 0 20px 45px rgba(0,0,0,0.85)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              zIndex: 10,
            }}
          >
            {/* Top-Right Specular Glass Reflection */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 90,
                height: 90,
                borderRadius: "0 42px 0 90px",
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(216, 255, 62, 0) 100%)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                fontSize: 42,
                fontWeight: 900,
                color: "#FFFFFF",
                lineHeight: 1,
              }}
            >
              SCL
            </span>
            <span
              style={{
                fontSize: 42,
                fontWeight: 900,
                color: neonLime,
                lineHeight: 1,
                marginTop: 4,
              }}
            >
              VSL
            </span>
          </div>

          {/* Product Subtitle */}
          <div
            style={{
              marginTop: 34,
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 34,
              fontWeight: 800,
              transform: `scale(${sp(190)})`,
              zIndex: 10,
            }}
          >
            <span>SCL VSL</span>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 44,
                color: neonLime,
              }}
            >
              Studio
            </span>
          </div>

          {/* Headline */}
          <div
            style={{
              marginTop: 26,
              fontSize: 54,
              fontWeight: 800,
              transform: `translateY(${interpolate(sp(200), [0, 1], [30, 0])}px)`,
              opacity: sp(200),
              zIndex: 10,
            }}
          >
            Di SCL VSL, cukup bayar{" "}
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 72,
                color: neonLime,
              }}
            >
              sekali.
            </span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 4: FRAMES 315 - 395 (10.50s - 13.17s) */}
      {/* "Tanpa limit. Tanpa kuota." + Desktop macOS Window Mockup */}
      {/* ========================================================================= */}
      {frame >= 315 && frame < 395 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 180,
          }}
        >
          {/* Headline */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 58,
              fontWeight: 800,
              transform: `translateY(${interpolate(sp(315), [0, 1], [30, 0])}px)`,
              opacity: sp(315),
            }}
          >
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 74,
              }}
            >
              Tanpa
            </span>
            <span>limit.</span>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 74,
                marginLeft: 14,
              }}
            >
              Tanpa
            </span>
            <span>kuota.</span>
          </div>

          {/* Desktop macOS Window Mockup */}
          <div
            style={{
              width: 950,
              height: 980,
              marginTop: 38,
              backgroundColor: "#101612",
              borderRadius: 24,
              border: "2px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 35px 70px rgba(0,0,0,0.9)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transform: `scale(${sp(320)})`,
            }}
          >
            {/* Window Title Header */}
            <div
              style={{
                height: 52,
                backgroundColor: "#17211B",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 22px",
              }}
            >
              {/* Traffic light dots */}
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <span style={{ fontSize: 18, fontWeight: 700, color: textMuted }}>
                15 klip — generate sepuasnya
              </span>
              <div style={{ width: 40 }} />
            </div>

            {/* URL Input Bar & Action */}
            <div
              style={{
                height: 60,
                backgroundColor: "#0D1410",
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                display: "flex",
                alignItems: "center",
                padding: "0 20px",
                gap: 14,
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 800, color: neonLime }}>
                SCL-VSL®
              </span>
              <div
                style={{
                  flex: 1,
                  height: 38,
                  backgroundColor: "#16201A",
                  borderRadius: 10,
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 14px",
                  fontSize: 15,
                  color: "#9EA9A2",
                }}
              >
                https://youtu.be/77k-ugMSyF0?si=50qa8RIryD9k
              </div>
              <div
                style={{
                  backgroundColor: neonLime,
                  color: "#000000",
                  padding: "8px 18px",
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 900,
                }}
              >
                Run pipeline
              </div>
            </div>

            {/* Main Editor Center (3-Columns) */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
              {/* Left Tool Palette */}
              <div
                style={{
                  width: 58,
                  borderRight: "1px solid rgba(255, 255, 255, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  paddingTop: 18,
                  gap: 22,
                  backgroundColor: "#0F1612",
                }}
              >
                <Scissors size={20} color={textMuted} />
                <Subtitles size={20} color={neonLime} />
                <Lock size={20} color={textMuted} />
                <Sparkles size={20} color={textMuted} />
                <Settings size={20} color={textMuted} />
              </div>

              {/* Center 9:16 Video Preview */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#080C0A",
                  padding: 16,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: 330,
                    height: 520,
                    borderRadius: 16,
                    backgroundColor: "#1A241F",
                    border: "1px solid rgba(255, 255, 255, 0.14)",
                    overflow: "hidden",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingBottom: 40,
                  }}
                >
                  <Img
                    src={staticFile("podcast_preview_crop.png")}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {/* Burned-In Dynamic Subtitle */}
                  <div
                    style={{
                      position: "relative",
                      zIndex: 10,
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      padding: "8px 18px",
                      borderRadius: 12,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: "italic",
                        fontSize: 28,
                        color: "#FFFFFF",
                      }}
                    >
                      Dia emang
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Panel: Caption Settings */}
              <div
                style={{
                  width: 320,
                  borderLeft: "1px solid rgba(255, 255, 255, 0.08)",
                  padding: "18px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  backgroundColor: "#0E1511",
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 900, color: neonLime }}>
                  CLIP SETTING
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: textMuted }}>
                  FONT CAPTION (BURN)
                </span>

                {/* 6 Preset Fonts Grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[
                    { name: "Ikut gaya", active: false },
                    { name: "Open Sauce", active: false },
                    { name: "Times New Roman", active: true },
                    { name: "Arial Narrow", active: false },
                    { name: "Pinyon Script", active: false },
                    { name: "Garamond", active: false },
                  ].map((fnt, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "10px 8px",
                        borderRadius: 10,
                        backgroundColor: fnt.active ? "rgba(216, 255, 62, 0.15)" : "#151F19",
                        border: fnt.active ? `2px solid ${neonLime}` : "1px solid rgba(255,255,255,0.08)",
                        fontSize: 12,
                        fontWeight: 700,
                        color: fnt.active ? neonLime : textWhite,
                        textAlign: "center",
                      }}
                    >
                      {fnt.name}
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    backgroundColor: "#16221B",
                    borderRadius: 12,
                    padding: "14px",
                    border: "1px dashed rgba(255,255,255,0.15)",
                    textAlign: "center",
                  }}
                >
                  <span style={{ fontSize: 13, color: textMuted }}>Contoh Caption:</span>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF", marginTop: 4 }}>
                    Contoh Caption
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Panel: Detected Clips Carousel */}
            <div
              style={{
                height: 190,
                backgroundColor: "#0D1310",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                padding: "12px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: textWhite }}>
                  DETECTED CLIPS: 15 (15 found | 15 rendered | 7 hot)
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, color: neonLime, fontWeight: 700 }}>● 100% Ready</span>
                  <div
                    style={{
                      backgroundColor: neonLime,
                      color: "#000000",
                      padding: "6px 16px",
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 900,
                    }}
                  >
                    Render all
                  </div>
                </div>
              </div>

              {/* Clip Thumbnail Cards Horizontal Scroll */}
              <div style={{ display: "flex", gap: 12, overflow: "hidden" }}>
                {[
                  { id: "01", score: "★ 8/10", dur: "00:30" },
                  { id: "02", score: "★ 7/10", dur: "00:28" },
                  { id: "03", score: "★ 9/10", dur: "00:35" },
                  { id: "04", score: "★ 8/10", dur: "00:31" },
                  { id: "05", score: "★ 9/10", dur: "00:30", active: true },
                  { id: "06", score: "★ 7/10", dur: "00:25" },
                ].map((c, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: 140,
                      height: 110,
                      borderRadius: 12,
                      backgroundColor: "#16201A",
                      border: c.active ? `2px solid ${neonLime}` : "1px solid rgba(255,255,255,0.12)",
                      padding: "8px 10px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      boxShadow: c.active ? `0 0 16px ${neonGlow}` : "none",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 900, color: c.active ? neonLime : textWhite }}>
                        #{c.id}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: neonLime }}>{c.score}</span>
                    </div>
                    <span style={{ fontSize: 11, color: textMuted }}>Mimpi Jadi Founder...</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#8E9992" }}>{c.dur}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 5: FRAMES 395 - 475 (13.17s - 15.83s) */}
      {/* "Kata member:" + Telegram Chat Testimonial */}
      {/* ========================================================================= */}
      {frame >= 395 && frame < 475 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 240,
          }}
        >
          {/* Headline */}
          <h1
            style={{
              fontSize: 66,
              fontWeight: 800,
              margin: 0,
              transform: `translateY(${interpolate(sp(395), [0, 1], [30, 0])}px)`,
              opacity: sp(395),
            }}
          >
            Kata member:
          </h1>

          {/* Telegram Chat Window Card */}
          <div
            style={{
              width: 900,
              marginTop: 50,
              backgroundColor: "#141C17",
              borderRadius: 28,
              border: cardBorder,
              padding: "36px 34px",
              boxShadow: "0 30px 65px rgba(0,0,0,0.85)",
              display: "flex",
              flexDirection: "column",
              gap: 24,
              transform: `scale(${sp(400)})`,
            }}
          >
            {/* Chat 1: Stillo */}
            <div style={{ display: "flex", gap: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#8B5CF6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#FFFFFF",
                }}
              >
                S
              </div>
              <div style={{ flex: 1, backgroundColor: "#1B2620", borderRadius: 16, padding: "14px 20px" }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#C4B5FD" }}>Stillo</span>
                <p style={{ margin: "6px 0 0", fontSize: 22, color: "#FFFFFF", fontWeight: 600 }}>
                  worth it gak yang sudah beli ini ?
                </p>
              </div>
            </div>

            {/* Chat 2: BONDANG */}
            <div style={{ display: "flex", gap: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#06B6D4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: 900,
                  color: "#FFFFFF",
                }}
              >
                B
              </div>
              <div style={{ flex: 1, backgroundColor: "#1B2620", borderRadius: 16, padding: "14px 20px" }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#67E8F9" }}>BONDANG</span>
                <p style={{ margin: "6px 0 0", fontSize: 22, color: "#FFFFFF", fontWeight: 600 }}>
                  permisi , mau tanya hasil yang udah di edit nih kan terus kita mau save gimana ya ?
                </p>
              </div>
            </div>

            {/* Chat 3: Stevanus Gunawan (The Highlighted Testimonial) */}
            <div
              style={{
                display: "flex",
                gap: 16,
                transform: `scale(${sp(412)})`,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#F43F5E",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#FFFFFF",
                }}
              >
                G
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: "#202E26",
                  borderRadius: 18,
                  padding: "16px 22px",
                  border: `2px solid ${neonLime}`,
                  boxShadow: `0 0 25px rgba(216, 255, 62, 0.25)`,
                }}
              >
                {/* Quoted Message Preview */}
                <div
                  style={{
                    borderLeft: "3px solid #8B5CF6",
                    paddingLeft: 12,
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#C4B5FD" }}>Stillo</span>
                  <div style={{ fontSize: 16, color: textMuted }}>worth it gak yang sudah beli ini ?</div>
                </div>

                <span style={{ fontSize: 20, fontWeight: 900, color: "#FDA4AF" }}>Stevanus Gunawan</span>
                <p style={{ margin: "6px 0 0", fontSize: 25, color: "#FFFFFF", fontWeight: 800 }}>
                  Worth banget jadi engga usa langganan apps kliper lagi
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 6: FRAMES 475 - 684 (15.83s - 22.80s) */}
      {/* "Cuma Rp167 ribu. *Lifetime.*" + Rp167.000 + 3 Checks + Instagram CTA */}
      {/* ========================================================================= */}
      {frame >= 475 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 180,
            paddingBottom: 80,
            justifyContent: "space-between",
          }}
        >
          {/* Top Hook Text */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `scale(${sp(475)})`,
            }}
          >
            <div style={{ fontSize: 62, fontWeight: 800 }}>
              Cuma <span style={{ color: neonLime }}>Rp167 ribu.</span>
            </div>
            <div
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 66,
                color: "#FFFFFF",
                marginTop: -4,
              }}
            >
              Lifetime.
            </div>
          </div>

          {/* Central Price & App Squircle Box */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `scale(${sp(482)})`,
            }}
          >
            {/* Small Glossy Icon */}
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 26,
                background: "linear-gradient(145deg, #1A241E 0%, #0C120F 100%)",
                border: `2px solid ${neonLime}`,
                boxShadow: `0 0 35px rgba(216, 255, 62, 0.4)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <span style={{ fontSize: 24, fontWeight: 900, color: "#FFFFFF", lineHeight: 1 }}>SCL</span>
              <span style={{ fontSize: 24, fontWeight: 900, color: neonLime, lineHeight: 1, marginTop: 2 }}>VSL</span>
            </div>

            {/* Massive Price */}
            <div
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 124,
                lineHeight: 0.95,
                color: "#FFFFFF",
                letterSpacing: "0.02em",
                textShadow: `0 0 45px ${neonGlow}`,
              }}
            >
              Rp167.000
            </div>

            {/* Pill Badge: LIFETIME · BAYAR 1x */}
            <div
              style={{
                marginTop: 20,
                backgroundColor: neonLime,
                color: "#000000",
                padding: "12px 34px",
                borderRadius: 30,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.06em",
                boxShadow: "0 6px 20px rgba(216, 255, 62, 0.4)",
              }}
            >
              LIFETIME • BAYAR 1x
            </div>
          </div>

          {/* 3 Checklist Features */}
          <div
            style={{
              width: 780,
              display: "flex",
              flexDirection: "column",
              gap: 22,
              transform: `scale(${sp(490)})`,
            }}
          >
            {[
              "Generate tanpa limit",
              "Update fitur gratis",
              "Komunitas Telegram 1000+",
            ].map((feat, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#FFFFFF",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: "rgba(216, 255, 62, 0.15)",
                    border: `2px solid ${neonLime}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Check size={26} color={neonLime} strokeWidth={3.5} />
                </div>
                <span>{feat}</span>
              </div>
            ))}
          </div>

          {/* Bottom Instagram CTA Button */}
          <div
            style={{
              width: 860,
              height: 94,
              backgroundColor: neonLime,
              borderRadius: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              boxShadow: `0 14px 40px rgba(216, 255, 62, 0.45)`,
              transform: `scale(${sp(500)})`,
            }}
          >
            <InstagramIcon size={40} color="#000000" />
            <span
              style={{
                fontSize: 36,
                fontWeight: 900,
                color: "#000000",
                letterSpacing: "-0.01em",
              }}
            >
              @soclvisuals
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
